import { put, takeEvery, all, call, race, delay, cancelled } from "redux-saga/effects";
import moment from "moment-timezone";
import { PICTREE, ENDPOINT } from "../../constants";
import {
  setPicTreeRegularSeasonPageData,
  setPicTreeRegularSeasonPageLoading,
  setPageTopLoader,
} from "actions";
import { getJwtToken, getDataFromRDS, getDataFromSxi } from "api";

const REGULAR_SEASON_UI_TO_GAME_SPACE_ID = {
  "regular-season-1": "cmp4yz2lj00017rtcc4ok0jgv",
};

const REGULAR_SEASON_MAX_BY_GAME_SPACE_ID = {
  cmp4yz2lj00017rtcc4ok0jgv: 812206,
};

const COMPANY_NAME_BY_CODE = {
  NTT_ME: "NTT ME",
  TEPCO: "東京電力 TEPCO",
  NTT_FT: "NTT FT",
  HEPCO: "ほくでん",
};

const COMPANY_SERIES_ORDER = ["NTT_ME", "TEPCO", "NTT_FT", "HEPCO"];
const REGULAR_SEASON_TREND_RDS_TABLE = "PicTreeKpiRegularSeasonDailyTrendAggProd";
const REGULAR_SEASON_TREND_COMPANY_CODE = "NTT_ME";
const REGULAR_SEASON_COMPANY_ACHIEVE_RDS_TABLE = "PicTreeKpiRegularSeasonCompanyAchieveAggProd";

const SXI_HEAVY_RETRY = {
  timeoutMs: 30000,
  retries: 3,
  backoffBase: 1500,
};

function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000,
    retries = 2,
    backoffBase = 1000,
    backoffFactor = 2,
    jitter = true,
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || "";
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    },
  } = opts;

  let attempt = 0;
  while (true) {
    attempt += 1;

    try {
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) {
        throw new Error(`timeout after ${timeoutMs}ms`);
      }
      return response;
    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err;
      }

      const wait = Math.min(backoffBase * Math.pow(backoffFactor, attempt - 1), 10000);
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
    } finally {
      if (yield cancelled()) {
        // no-op
      }
    }
  }
}

const formatNumber = (value) => Number(value || 0).toLocaleString("ja-JP");

const toNumber = (rows, key) => {
  const raw = rows && rows[0] ? rows[0][key] : 0;
  return Number(raw || 0);
};

const resolveGameSpaceId = (selectedSeasonId) => (
  REGULAR_SEASON_UI_TO_GAME_SPACE_ID[selectedSeasonId] || selectedSeasonId || "cmrz0wo8t0000bpzcana86bow"
);

const resolveMaxNumber = (gameSpaceId) => REGULAR_SEASON_MAX_BY_GAME_SPACE_ID[gameSpaceId] || 0;

const resolveSeasonPeriodLabel = (selectedSeasonId) => {
  const match = String(selectedSeasonId || "").match(/regular-season-(\d+)/);
  return match ? `RS${match[1]}` : String(selectedSeasonId || "");
};

const normalizeCompanyCode = (companyCode) => String(companyCode || "").replace(/-/g, "_").toUpperCase();

const resolveCompanyName = (companyCode) => {
  const normalized = normalizeCompanyCode(companyCode);
  return COMPANY_NAME_BY_CODE[normalized] || companyCode || "不明";
};

const resolveRegularSeasonLabel = (seasonId, seasonName) => {
  const explicitName = String(seasonName || "").trim();
  if (explicitName) {
    return explicitName;
  }
  const match = String(seasonId || "").match(/regular-season-(\d+)/);
  return match ? `レギュラーシーズン${match[1]}` : String(seasonId || "");
};

const emptyTrend = () => ({
  date: [],
  uniqueShots: [],
  reviewerOk: [],
  uniqueShooters: [],
});

const emptyKpis = () => ([
  {
    key: "uniqueShot",
    title: "ユニーク撮影本数",
    value: "--",
  },
  {
    key: "achievementRate",
    title: "撮影達成率",
    value: "--",
    subtitle: "対象 --本",
  },
  {
    key: "reviewOk",
    title: "レビューOK基数",
    value: "--",
    subtitle: "OK率 --%",
  },
  {
    key: "uniqueShooter",
    title: "ユニーク撮影者数",
    value: "--",
    subtitle: "累計参加者",
  },
  {
    key: "firstShooter",
    title: "初回撮影者数",
    value: "--",
    subtitle: "今期新規",
  },
]);

const emptySeasonChart = () => ({
  labels: [],
  series: [],
});

const emptyRegularSeasonPageData = (selectedSeasonId) => ({
  selectedSeasonId,
  kpis: emptyKpis(),
  trend: emptyTrend(),
  trendLoading: true,
  companyRowsLoading: true,
  seasonChart: emptySeasonChart(),
  seasonChartLoading: true,
  companyRows: [],
});

const enumerateDaysJst = (startJst, endJst) => {
  const days = [];
  const start = moment.tz(startJst, "Asia/Tokyo").startOf("day");
  const end = moment.tz(endJst, "Asia/Tokyo").startOf("day");
  let current = start.clone();

  while (current.isSameOrBefore(end, "day")) {
    days.push(current.format("YYYY-MM-DD"));
    current = current.add(1, "day");
  }

  return days;
};

const normalizeDailyRows = (rows, dayKey = "day_jst", countKey = "cnt") => {
  const result = new Map();
  (rows || []).forEach((row) => {
    const rawDay = row[dayKey];
    const day = rawDay ? String(rawDay).slice(0, 10) : "";
    result.set(day, Number(row[countKey]) || 0);
  });
  return result;
};

const normalizeRdsRegularSeasonTrendRows = (rows) => {
  const parsedRows = JSON.parse(rows?.body || "[]");

  return parsedRows.map((row) => ({
    target_date_jst: row?.[0]?.stringValue || null,
    unique_shot_count: row?.[1]?.longValue ?? row?.[1]?.doubleValue ?? row?.[1]?.stringValue ?? null,
    review_ok_count: row?.[2]?.longValue ?? row?.[2]?.doubleValue ?? row?.[2]?.stringValue ?? null,
    unique_shooter_count: row?.[3]?.longValue ?? row?.[3]?.doubleValue ?? row?.[3]?.stringValue ?? null,
  }));
};

const normalizeRdsRegularSeasonCompanyRows = (rows) => {
  const parsedRows = JSON.parse(rows?.body || "[]");

  return parsedRows.map((row) => ({
    company_code: row?.[0]?.stringValue || null,
    unique_shots: row?.[1]?.longValue ?? row?.[1]?.doubleValue ?? row?.[1]?.stringValue ?? null,
    review_ok_count: row?.[2]?.longValue ?? row?.[2]?.doubleValue ?? row?.[2]?.stringValue ?? null,
    batch_day_jst: row?.[3]?.stringValue || null,
    batch_run_at_jst: row?.[4]?.stringValue || null,
  }));
};

const pickLatestCompanyRows = (rows) => {
  const latestByCompany = new Map();

  (rows || []).forEach((row) => {
    const companyCode = row.company_code;
    if (!companyCode || latestByCompany.has(companyCode)) {
      return;
    }

    latestByCompany.set(companyCode, row);
  });

  return Array.from(latestByCompany.values());
};

const buildRegularSeasonSummaryKpis = ({ countDenchu, countReviewOk, countUniqueUser, countNewUser, maxNumber }) => {
  const achievementRate = maxNumber > 0 ? (countReviewOk / maxNumber) * 100 : 0;
  const okRate = countDenchu > 0 ? (countReviewOk / countDenchu) * 100 : 0;

  return [
    {
      key: "uniqueShot",
      title: "ユニーク撮影本数",
      value: formatNumber(countDenchu),
    },
    {
      key: "achievementRate",
      title: "撮影達成率",
      value: `${achievementRate.toFixed(1)}%`,
      subtitle: `対象 ${formatNumber(maxNumber)}本`,
    },
    {
      key: "reviewOk",
      title: "レビューOK基数",
      value: formatNumber(countReviewOk),
      subtitle: `OK率 ${okRate.toFixed(1)}%`,
    },
    {
      key: "uniqueShooter",
      title: "ユニーク撮影者数",
      value: formatNumber(countUniqueUser),
      subtitle: "累計参加者",
    },
    {
      key: "firstShooter",
      title: "初回撮影者数",
      value: formatNumber(countNewUser),
      subtitle: "今期新規",
    },
  ];
};

function* fetchRegularSeasonSummary(selectedSeasonId) {
  const jwtToken = yield call(getJwtToken);
  const gameSpaceId = resolveGameSpaceId(selectedSeasonId);
  const maxNumber = resolveMaxNumber(gameSpaceId);

  const sqlQueryCountPhotoDenchu = {
    query: `
      SELECT COUNT(DISTINCT pae.power_asset_id) AS total_photo_power_assets
      FROM power_asset_event pae
      WHERE pae.game_space_id = '${gameSpaceId}'
        AND pae.type = 'PHOTOGRAPH';
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const sqlQueryCountUniqueUser = {
    query: `
      SELECT COUNT(DISTINCT rr.player_id) AS unique_photographers
      FROM review_request_event rre
      JOIN review_request rr ON rr.id = rre.review_request_id
      JOIN review_request_event_of_approved rreoa ON rreoa.id = rre.id
      WHERE rr.game_space_id = '${gameSpaceId}'
        AND rre.type = 'APPROVED';
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const sqlQueryCountReviewOk = {
    query: `
      SELECT COUNT(DISTINCT rr.power_asset_id) AS reviewOk_count
      FROM review_request_event rre
      JOIN review_request rr ON rre.review_request_id = rr.id
      WHERE rre.type = 'APPROVED'
        AND rre.game_space_id = '${gameSpaceId}';
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const sqlQueryCountNewUsers = {
    query: `
      SELECT COUNT(*) AS first_time_photographers
      FROM (
        SELECT DISTINCT rr.player_id
        FROM review_request_event rre
        JOIN review_request rr ON rr.id = rre.review_request_id
        JOIN review_request_event_of_approved rreoa ON rreoa.id = rre.id
        WHERE rr.game_space_id = '${gameSpaceId}'
          AND rre.type = 'APPROVED'
        GROUP BY rr.player_id
        HAVING MIN(rre.occurred_datetime) >= (
          SELECT gt.start_datetime
          FROM game_space gs
          JOIN game_term gt ON gt.id = gs.game_term_id
          WHERE gs.id = '${gameSpaceId}'
        )
        AND MIN(rre.occurred_datetime) < (
          SELECT gt.end_datetime
          FROM game_space gs
          JOIN game_term gt ON gt.id = gs.game_term_id
          WHERE gs.id = '${gameSpaceId}'
        )
      ) AS c
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const [
    reviewOkRows,
    photoDenchuRows,
    uniqueUserRows,
    newUserRows,
  ] = yield all([
    call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [sqlQueryCountReviewOk, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    ),
    call(
      callWithTimeoutAndRetry,
      getDataFromRDS,
      [sqlQueryCountPhotoDenchu, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    ),
    call(
      callWithTimeoutAndRetry,
      getDataFromRDS,
      [sqlQueryCountUniqueUser, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    ),
    call(
      callWithTimeoutAndRetry,
      getDataFromRDS,
      [sqlQueryCountNewUsers, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    ),
  ]);

  const countDenchu = toNumber(photoDenchuRows, "total_photo_power_assets");
  const countReviewOk = toNumber(reviewOkRows, "reviewOk_count");
  const countUniqueUser = toNumber(uniqueUserRows, "unique_photographers");
  const countNewUser = toNumber(newUserRows, "first_time_photographers");

  return {
    selectedSeasonId,
    kpis: buildRegularSeasonSummaryKpis({
      countDenchu,
      countReviewOk,
      countUniqueUser,
      countNewUser,
      maxNumber,
    }),
  };
}

function* fetchRegularSeasonTrend(selectedSeasonId) {
  const jwtToken = yield call(getJwtToken);
  const gameSpaceId = resolveGameSpaceId(selectedSeasonId);

  const seasonRangeQuery = {
    query: `
      SELECT
        DATE_FORMAT(CONVERT_TZ(gt.start_datetime, 'UTC', 'Asia/Tokyo'), '%Y-%m-%d') AS start_jst,
        DATE_FORMAT(CONVERT_TZ(gt.end_datetime, 'UTC', 'Asia/Tokyo'), '%Y-%m-%d') AS end_jst
      FROM game_space gs
      JOIN game_term gt ON gt.id = gs.game_term_id
      WHERE gs.id = '${gameSpaceId}';
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const [seasonRangeRows] = yield all([
    call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [seasonRangeQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    ),
  ]);

  const seasonStartJst = seasonRangeRows?.[0]?.start_jst;
  const seasonEndJst = seasonRangeRows?.[0]?.end_jst;

  if (!seasonStartJst || !seasonEndJst) {
    return emptyTrend();
  }

  const todayJst = moment.tz("Asia/Tokyo").format("YYYY-MM-DD");
  const trendEndJst = moment.min(
    moment.tz(seasonEndJst, "Asia/Tokyo").startOf("day"),
    moment.tz(todayJst, "Asia/Tokyo").startOf("day")
  ).format("YYYY-MM-DD");

  const trendQuery = {
    query: `
      SELECT
        targetDateJst AS target_date_jst,
        uniqueShotCount AS unique_shot_count,
        reviewOkCount AS review_ok_count,
        uniqueShooterCount AS unique_shooter_count
      FROM ${REGULAR_SEASON_TREND_RDS_TABLE}
      WHERE seasonId = '${gameSpaceId}'
        AND companyCode = '${REGULAR_SEASON_TREND_COMPANY_CODE}'
        AND targetDateJst >= '${seasonStartJst}'
        AND targetDateJst <= '${trendEndJst}'
      ORDER BY targetDateJst ASC;
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const trendRows = yield call(
    callWithTimeoutAndRetry,
    getDataFromRDS,
    [trendQuery, ENDPOINT.GET_DATA_RDS, jwtToken],
    SXI_HEAVY_RETRY
  );

  const days = enumerateDaysJst(seasonStartJst, trendEndJst);
  const normalizedTrendRows = normalizeRdsRegularSeasonTrendRows(trendRows);
  const uniqueShotsMap = normalizeDailyRows(normalizedTrendRows, "target_date_jst", "unique_shot_count");
  const reviewOkMap = normalizeDailyRows(normalizedTrendRows, "target_date_jst", "review_ok_count");
  const uniqueShootersMap = normalizeDailyRows(normalizedTrendRows, "target_date_jst", "unique_shooter_count");

  return {
    date: days.map((day) => moment(day).format("M/D")),
    uniqueShots: days.map((day) => uniqueShotsMap.get(day) || 0),
    reviewerOk: days.map((day) => reviewOkMap.get(day) || 0),
    uniqueShooters: days.map((day) => uniqueShootersMap.get(day) || 0),
  };
}

function* fetchRegularSeasonCompanyRows(selectedSeasonId) {
  const jwtToken = yield call(getJwtToken);
  const gameSpaceId = resolveGameSpaceId(selectedSeasonId);
  const maxNumber = resolveMaxNumber(gameSpaceId);
  const periodLabel = resolveSeasonPeriodLabel(selectedSeasonId);

  const companyRowsQuery = {
    query: `
      SELECT
        companyCode AS company_code,
        uniqueShots AS unique_shots,
        reviewOkCount AS review_ok_count,
        batchDayJst AS batch_day_jst,
        batchRunAtJst AS batch_run_at_jst
      FROM ${REGULAR_SEASON_COMPANY_ACHIEVE_RDS_TABLE}
      WHERE seasonId = '${gameSpaceId}'
      ORDER BY companyCode ASC, batchDayJst DESC, batchRunAtJst DESC, createdAt DESC;
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const companyRowsRows = yield call(
    callWithTimeoutAndRetry,
    getDataFromRDS,
    [companyRowsQuery, ENDPOINT.GET_DATA_RDS, jwtToken],
    SXI_HEAVY_RETRY
  );

  const normalizedRows = normalizeRdsRegularSeasonCompanyRows(companyRowsRows);
  const latestRows = pickLatestCompanyRows(normalizedRows).sort((a, b) => {
    const reviewDiff = (Number(b.review_ok_count) || 0) - (Number(a.review_ok_count) || 0);
    if (reviewDiff !== 0) {
      return reviewDiff;
    }

    const shotDiff = (Number(b.unique_shots) || 0) - (Number(a.unique_shots) || 0);
    if (shotDiff !== 0) {
      return shotDiff;
    }

    return String(a.company_code || "").localeCompare(String(b.company_code || ""));
  });

  return latestRows.map((row) => {
    const reviewOkCount = Number(row.review_ok_count) || 0;
    const rate = maxNumber > 0 ? (reviewOkCount / maxNumber) * 100 : 0;

    return {
      companyCode: row.company_code,
      companyName: resolveCompanyName(row.company_code),
      rate,
      progress: rate,
      period: periodLabel,
    };
  });
}

function* fetchRegularSeasonSeasonChart() {
  const jwtToken = yield call(getJwtToken);

  const seasonListQuery = {
    query: `
      SELECT
        gs.id AS season_id,
        sn.official_name AS season_name
      FROM game_space gs
      JOIN game_term gt ON gt.id = gs.game_term_id
      JOIN season_name sn ON sn.season_id = gs.id AND sn.language_code = 'JA'
      WHERE sn.official_name LIKE 'レギュラーシーズン%'
      ORDER BY gt.start_datetime ASC;
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const seasonCountsQuery = {
    query: `
      SELECT
        grouped.season_id,
        grouped.company_code,
        COUNT(*) AS cnt
      FROM (
        SELECT DISTINCT pae.game_space_id AS season_id, 'NTT_ME' AS company_code, pae.power_asset_id
        FROM power_asset_event pae
        JOIN power_asset_detail_of_ntt_me pad ON pad.power_asset_id = pae.power_asset_id
        JOIN season_name sn ON sn.season_id = pae.game_space_id AND sn.language_code = 'JA'
        WHERE pae.type = 'PHOTOGRAPH'
          AND sn.official_name LIKE 'レギュラーシーズン%'
      ) grouped
      GROUP BY grouped.season_id, grouped.company_code;
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const [seasonListRows, seasonCountRows] = yield all([
    call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [seasonListQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    ),
    call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [seasonCountsQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    ),
  ]);

  const seasons = (seasonListRows || []).map((row) => ({
    seasonId: row.season_id,
    seasonLabel: resolveRegularSeasonLabel(row.season_id, row.season_name),
  }));

  if (!seasons.length) {
    return {
      labels: [],
      series: [],
    };
  }

  const seasonLabelMap = new Map(seasons.map((item) => [item.seasonId, item.seasonLabel]));
  const seasonOrder = seasons.map((item) => item.seasonId);
  const countMap = new Map();

  (seasonCountRows || []).forEach((row) => {
    const seasonId = row.season_id;
    const companyCode = normalizeCompanyCode(row.company_code);
    const cnt = Number(row.cnt) || 0;
    countMap.set(`${seasonId}:${companyCode}`, cnt);
  });

  const labels = seasonOrder.map((seasonId) => seasonLabelMap.get(seasonId) || seasonId);
  const series = COMPANY_SERIES_ORDER
    .filter((companyCode) => seasonOrder.some((seasonId) => (countMap.get(`${seasonId}:${companyCode}`) || 0) > 0))
    .map((companyCode) => ({
      name: resolveCompanyName(companyCode),
      data: seasonOrder.map((seasonId) => countMap.get(`${seasonId}:${companyCode}`) || 0),
    }));

  return {
    labels,
    series,
  };
}

function* fetchAndStoreRegularSeasonSection(
  sectionKey,
  fetcher,
  selectedSeasonId,
  fallbackValue,
  errorLabel,
  extractValue = (value) => value,
  loadingKey
) {
  try {
    if (loadingKey) {
      yield put(setPicTreeRegularSeasonPageData({
        [loadingKey]: true,
      }));
    }
    const rawValue = yield call(fetcher, selectedSeasonId);
    const value = extractValue(rawValue);
    yield put(setPicTreeRegularSeasonPageData({
      [sectionKey]: value,
    }));
    if (loadingKey) {
      yield put(setPicTreeRegularSeasonPageData({
        [loadingKey]: false,
      }));
    }
    return value;
  } catch (error) {
    console.log(`[pictreeRegularSeasonSaga] ${errorLabel} load failed`, error);
    const payload = {
      [sectionKey]: fallbackValue,
    };
    if (loadingKey) {
      payload[loadingKey] = false;
    }
    yield put(setPicTreeRegularSeasonPageData(payload));
    return fallbackValue;
  }
}

function* fetchRegularSeasonBottomSections(selectedSeasonId) {
  yield all([
    call(
      fetchAndStoreRegularSeasonSection,
      "companyRows",
      fetchRegularSeasonCompanyRows,
      selectedSeasonId,
      [],
      "company rows",
      (value) => value,
      "companyRowsLoading"
    ),
    call(
      fetchAndStoreRegularSeasonSection,
      "seasonChart",
      fetchRegularSeasonSeasonChart,
      selectedSeasonId,
      emptySeasonChart(),
      "season chart",
      (value) => value,
      "seasonChartLoading"
    ),
  ]);
}

export function* handleRegularSeasonPageData(action) {
  try {
    yield put(setPicTreeRegularSeasonPageLoading(true));
    yield put(setPageTopLoader(true));
    const selectedSeasonId = action?.payload?.selectedSeasonId || "regular-season-1";
    yield put(setPicTreeRegularSeasonPageData(emptyRegularSeasonPageData(selectedSeasonId)));

    yield all([
      call(
        fetchAndStoreRegularSeasonSection,
        "kpis",
        fetchRegularSeasonSummary,
        selectedSeasonId,
        emptyKpis(),
        "summary",
        (value) => value.kpis || emptyKpis()
      ),
      call(
        fetchAndStoreRegularSeasonSection,
        "trend",
        fetchRegularSeasonTrend,
        selectedSeasonId,
        emptyTrend(),
        "trend",
        (value) => value,
        "trendLoading"
      ),
      call(
        fetchAndStoreRegularSeasonSection,
        "companyRows",
        fetchRegularSeasonCompanyRows,
        selectedSeasonId,
        [],
        "company rows",
        (value) => value,
        "companyRowsLoading"
      ),
      call(
        fetchAndStoreRegularSeasonSection,
        "seasonChart",
        fetchRegularSeasonSeasonChart,
        selectedSeasonId,
        emptySeasonChart(),
        "season chart",
        (value) => value,
        "seasonChartLoading"
      ),
    ]);
  } catch (error) {
    console.log("[pictreeRegularSeasonSaga] failed to load summary", error);
  } finally {
    yield put(setPageTopLoader(false));
    yield put(setPicTreeRegularSeasonPageLoading(false));
  }
}

export default function* pictreeRegularSeasonSaga() {
  yield takeEvery(PICTREE.GET_REGULAR_SEASON_PAGE_DATA, handleRegularSeasonPageData);
}
